import { data as withStatus, useParams } from 'react-router'
import type { Route } from './+types/MaterialPage'
import { PageShell } from '@/components/layout/PageShell'
import { LocaleLink as Link } from '@/components/LocaleLink'
import { LexicalRender } from '@/components/ui/LexicalRender'
import { useMaterial } from '@/lib/cms/hooks'
import { createContentLoader } from '@/lib/cms/prefetch.server'
import { contentQueries } from '@/lib/cms/queries'
import { formatDate } from '@/lib/formatDate'
import { useTranslation } from '@/lib/i18n/LocaleContext'
import { messages } from '@/lib/i18n/messages'
import { localeFromParams } from '@/lib/routeLocale'
import { buildMeta, type PageSeo } from '@/lib/seo'
import NotFoundPage from '@/pages/NotFoundPage'

export async function loader({ params }: Route.LoaderArgs) {
  const locale = localeFromParams(params)
  const content = createContentLoader(locale)
  const material = await content.fetch(contentQueries.material(params.slug))
  if (!material) {
    const seo: PageSeo = { title: messages[locale].pageTitles.notFound, noindex: true }
    return withStatus({ notFound: true, dehydratedState: content.dehydrate(), seo }, { status: 404 })
  }
  const seo: PageSeo = { title: material.title, description: material.summary }
  return { notFound: false, dehydratedState: content.dehydrate(), seo }
}

export const meta: Route.MetaFunction = (args) => buildMeta(args, args.data?.seo)

const fileSize = (bytes?: number) => (bytes ? `${Math.max(1, Math.round(bytes / 1024))} kB` : '')

export default function MaterialRoute({ loaderData }: Route.ComponentProps) {
  const { slug = '' } = useParams()
  return loaderData.notFound ? <NotFoundPage /> : <MaterialPage slug={slug} />
}

function MaterialPage({ slug }: { slug: string }) {
  const { t, locale } = useTranslation()
  const { data: m, loading, error } = useMaterial(slug)

  return (
    <PageShell>
      <article className="block-tight">
        <div className="wrap wrap-narrow">
          <p className="small"><Link to="/materialer" className="back-link">← {t.app.backToList}</Link></p>
          {loading && <p className="muted">{t.common.loading}</p>}
          {error && <p className="error-text">{t.common.error}</p>}
          {m && (
            <>
              {m.category && <p className="eyebrow">{m.category.name}</p>}
              <h1 className="h1">{m.title}</h1>
              {m.summary && <p className="lede">{m.summary}</p>}
              {m.cover && <img className="cover" src={m.cover.url} alt={m.cover.alt} width={m.cover.width} height={m.cover.height} />}
              {m.body ? <div className="rich-text"><LexicalRender data={m.body} /></div> : null}

              {m.attachments.length > 0 && (
                <section className="attachments">
                  <h2 className="h3">{t.app.attachments}</h2>
                  <ul>
                    {m.attachments.map((a) => (
                      <li key={a.id}>
                        <a href={a.url} target="_blank" rel="noreferrer" className="attachment">
                          <span aria-hidden="true" className="attachment-icon">PDF</span>
                          <span className="attachment-title">{a.title}</span>
                          <span className="muted small">{t.app.download} {fileSize(a.filesize)}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <p className="muted small meta-line">
                {m.reviewedAt && <>{t.app.reviewed}: {formatDate(m.reviewedAt, locale)} · </>}
                {t.common.lastUpdated}: {formatDate(m.updatedAt, locale)}
              </p>
            </>
          )}
        </div>
      </article>
    </PageShell>
  )
}
